import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { TrendingUp, DollarSign, Percent, CheckCircle, AlertTriangle } from 'lucide-react';
import './MerchantAnalytics.css';

const PERIODS = [
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: '90d', label: '90d' },
];

const COLORS = ['#f7931a', '#627eea', '#345d9d', '#26a17b', '#2775ca', '#8a92b2', '#e84142'];

const fmtMoney = (n, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(n || 0);
  } catch {
    return `$${(n || 0).toFixed(2)}`;
  }
};

const MerchantAnalytics = ({ compact = false }) => {
  const [period, setPeriod] = useState(compact ? '30d' : '30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/merchant/analytics?period=${period}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (r.ok) {
          setData(await r.json());
        } else {
          setError('Failed to load analytics');
        }
      } catch (e) {
        console.error(e);
        setError('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [period]);

  if (loading) return <div className="ma-loading" data-testid="analytics-loading">Loading analytics...</div>;
  if (error) return <div className="ma-error">{error}</div>;
  if (!data) return null;

  const { summary, timeseries, top_crypto } = data;

  // Compact view: just the key chart for Overview
  if (compact) {
    return (
      <div className="ma-compact" data-testid="analytics-compact">
        <div className="ma-compact-header">
          <div className="ma-compact-title">
            <TrendingUp size={18} /> Revenue Trend
          </div>
          <div className="ma-period-selector compact">
            {PERIODS.map(({ key, label }) => (
              <button
                key={key}
                className={`ma-period-btn ${period === key ? 'active' : ''}`}
                onClick={() => setPeriod(key)}
                data-testid={`period-compact-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="ma-compact-stats">
          <div className="ma-stat-mini">
            <span>Gross</span><strong>{fmtMoney(summary.gross_volume)}</strong>
          </div>
          <div className="ma-stat-mini">
            <span>Net</span><strong>{fmtMoney(summary.net_volume)}</strong>
          </div>
          <div className="ma-stat-mini">
            <span>Completed</span><strong>{summary.completed_payments}</strong>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={140}>
          <LineChart data={timeseries}>
            <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={10} stroke="#888" />
            <YAxis fontSize={10} stroke="#888" />
            <Tooltip contentStyle={{ background: '#1f2937', border: 'none', borderRadius: 8 }} />
            <Line type="monotone" dataKey="gross" stroke="#f7931a" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="ma-root" data-testid="merchant-analytics">
      <div className="ma-header">
        <h2><TrendingUp size={22} /> Payment Analytics</h2>
        <div className="ma-period-selector">
          {PERIODS.map(({ key, label }) => (
            <button
              key={key}
              className={`ma-period-btn ${period === key ? 'active' : ''}`}
              onClick={() => setPeriod(key)}
              data-testid={`period-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="ma-stat-grid">
        <div className="ma-stat-card">
          <div className="ma-stat-icon" style={{ background: '#f59e0b' }}><DollarSign size={20} /></div>
          <div>
            <div className="ma-stat-value">{fmtMoney(summary.gross_volume)}</div>
            <div className="ma-stat-label">Gross volume</div>
          </div>
        </div>
        <div className="ma-stat-card">
          <div className="ma-stat-icon" style={{ background: '#22c55e' }}><CheckCircle size={20} /></div>
          <div>
            <div className="ma-stat-value">{fmtMoney(summary.net_volume)}</div>
            <div className="ma-stat-label">Net (after fees)</div>
          </div>
        </div>
        <div className="ma-stat-card">
          <div className="ma-stat-icon" style={{ background: '#3b82f6' }}><TrendingUp size={20} /></div>
          <div>
            <div className="ma-stat-value">{summary.completed_payments}</div>
            <div className="ma-stat-label">Completed ({summary.completion_rate}%)</div>
          </div>
        </div>
        <div className="ma-stat-card">
          <div className="ma-stat-icon" style={{ background: '#8b5cf6' }}><Percent size={20} /></div>
          <div>
            <div className="ma-stat-value">{fmtMoney(summary.avg_ticket)}</div>
            <div className="ma-stat-label">Avg. ticket</div>
          </div>
        </div>
        <div className="ma-stat-card">
          <div className="ma-stat-icon" style={{ background: '#ef4444' }}><AlertTriangle size={20} /></div>
          <div>
            <div className="ma-stat-value">{summary.avg_variance_pct}%</div>
            <div className="ma-stat-label">
              Avg variance ({summary.min_variance_pct}% / {summary.max_variance_pct}%)
            </div>
          </div>
        </div>
      </div>

      <div className="ma-chart-row">
        <div className="ma-chart-box">
          <h3>Daily Volume</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={timeseries}>
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} stroke="#888" />
              <YAxis fontSize={11} stroke="#888" />
              <Tooltip contentStyle={{ background: '#1f2937', border: 'none', borderRadius: 8 }} />
              <Legend />
              <Line type="monotone" dataKey="gross" stroke="#f7931a" name="Gross" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="net" stroke="#22c55e" name="Net" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="ma-chart-box">
          <h3>Top Crypto Methods</h3>
          {top_crypto.length === 0 ? (
            <div className="ma-empty">No completed payments in this period</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={top_crypto}
                  dataKey="volume"
                  nameKey="currency"
                  outerRadius={80}
                  label={({ currency, percent }) => `${currency} ${(percent * 100).toFixed(0)}%`}
                >
                  {top_crypto.map((entry, idx) => (
                    <Cell key={entry.currency} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: '#1f2937', border: 'none', borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="ma-chart-box">
        <h3>Daily Counts (Created vs Completed)</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={timeseries}>
            <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} stroke="#888" />
            <YAxis fontSize={11} stroke="#888" />
            <Tooltip contentStyle={{ background: '#1f2937', border: 'none', borderRadius: 8 }} />
            <Legend />
            <Bar dataKey="count" fill="#3b82f6" name="Created" />
            <Bar dataKey="completed" fill="#22c55e" name="Completed" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MerchantAnalytics;
